import type { ColumnSizingState } from '@tanstack/react-table';

type SizingColumn = { id: string; size?: number; minSize?: number; maxSize?: number };
const flexible: Record<string, { weight: number; ceiling: number }> = {
  title: { weight: 3, ceiling: Infinity },
  project: { weight: 2, ceiling: 380 },
  linked: { weight: 1.5, ceiling: 340 },
  tags: { weight: 1, ceiling: 260 },
};

export function isFlexibleAgentColumn(id: string) {
  return !!flexible[id];
}

/** Layout depends on container/column preferences, never the current rows. */
export function agentTableSizing(
  columns: SizingColumn[],
  manual: ColumnSizingState,
  available: number
): ColumnSizingState {
  const widths: ColumnSizingState = {};
  const automatic = columns.filter(
    column => manual[column.id] === undefined && flexible[column.id]
  );
  for (const column of columns) {
    widths[column.id] = Math.max(
      column.minSize ?? 0,
      Math.min(
        column.maxSize ?? (flexible[column.id] ? Infinity : 800),
        manual[column.id] ?? column.size ?? 150
      )
    );
  }
  if (!available) return widths;
  const proportional = columns.filter(column => flexible[column.id]);
  if (proportional.some(column => manual[column.id] !== undefined)) {
    // Saved widths act as weights in the text-column space. Compact columns keep
    // their pixel sizes; hidden columns do not consume any of the budget.
    let budget =
      available -
      columns
        .filter(column => !flexible[column.id])
        .reduce((sum, column) => sum + widths[column.id], 0);
    let pending = [...proportional];
    while (pending.length) {
      const weight = pending.reduce((sum, column) => sum + widths[column.id], 0);
      const targets = Object.fromEntries(
        pending.map(column => [column.id, weight ? (budget * widths[column.id]) / weight : 0])
      );
      const belowMinimum = pending.filter(column => targets[column.id] < (column.minSize ?? 0));
      const constrained = belowMinimum.length
        ? belowMinimum
        : pending.filter(column => targets[column.id] > (column.maxSize ?? Infinity));
      if (!constrained.length) {
        for (const column of pending) widths[column.id] = (budget * widths[column.id]) / weight;
        break;
      }
      for (const column of constrained) {
        widths[column.id] = Math.max(
          column.minSize ?? 0,
          Math.min(column.maxSize ?? Infinity, targets[column.id])
        );
        budget -= widths[column.id];
      }
      pending = pending.filter(column => !constrained.includes(column));
    }
    return widths;
  }
  let remaining = available - Object.values(widths).reduce((sum, width) => sum + width, 0);
  if (remaining < 0) {
    const capacity = automatic.reduce(
      (sum, column) => sum + widths[column.id] - (column.minSize ?? 0),
      0
    );
    const shrink = Math.min(-remaining, capacity);
    for (const column of automatic) {
      widths[column.id] -= capacity
        ? (shrink * (widths[column.id] - (column.minSize ?? 0))) / capacity
        : 0;
    }
  } else {
    let growing = automatic.filter(column => widths[column.id] < flexible[column.id].ceiling);
    while (remaining > 0.01 && growing.length) {
      const weight = growing.reduce((sum, column) => sum + flexible[column.id].weight, 0);
      const budget = remaining;
      for (const column of growing) {
        const extra = Math.min(
          (budget * flexible[column.id].weight) / weight,
          flexible[column.id].ceiling - widths[column.id],
          (column.maxSize ?? 800) - widths[column.id]
        );
        widths[column.id] += extra;
        remaining -= extra;
      }
      growing = growing.filter(
        column => widths[column.id] < Math.min(flexible[column.id].ceiling, column.maxSize ?? 800)
      );
    }
  }
  return widths;
}

/** Freeze the visible text proportions, keeping the dragged width exact. */
export function agentTableResize(
  columns: SizingColumn[],
  current: ColumnSizingState,
  next: ColumnSizingState,
  previous: ColumnSizingState,
  available: number
): ColumnSizingState {
  const changed = columns.filter(column => next[column.id] !== current[column.id]);
  if (!changed.length) return previous;
  if (
    available <= 0 ||
    (!changed.some(column => flexible[column.id]) &&
      !columns.some(column => flexible[column.id] && previous[column.id] !== undefined))
  ) {
    return {
      ...previous,
      ...Object.fromEntries(changed.map(column => [column.id, next[column.id]])),
    };
  }
  const pinned = new Set(changed.map(column => column.id));
  const others = agentTableSizing(
    columns.filter(column => !pinned.has(column.id)),
    current,
    available - changed.reduce((sum, column) => sum + next[column.id], 0)
  );
  return {
    ...previous,
    ...Object.fromEntries(
      columns
        .filter(column => flexible[column.id] || pinned.has(column.id))
        .map(column => [column.id, pinned.has(column.id) ? next[column.id] : others[column.id]])
    ),
  };
}
